/**
 * Presence: who else is in this room, and where they are looking.
 *
 * Rides the Yjs **awareness** channel, which the stack already carries end to
 * end — y-websocket creates an Awareness instance on the provider, and the
 * Python sync service's YRoom relays awareness frames between clients. Awareness
 * lives outside the document, so none of this touches the edit, the update log,
 * the undo stack or the snapshot writer. Nothing here needs a backend change.
 *
 * Identity is **client-asserted**: the sync handshake authorizes the connection
 * but does not pass an identity into the room, so a collab-link visitor can
 * claim any name. Consistent with "no ACL model in v1" — presence is a courtesy
 * signal, not an authentication one.
 */
import type { Awareness } from 'y-protocols/awareness';
import { create } from 'zustand';
import { usePlayback } from './playback';
import { useSession } from './session';

/**
 * Playhead broadcast interval. The engine writes `currentTime` every animation
 * frame during playback (~60Hz), so broadcasting each change would be ~60
 * messages/second/client. 100ms is smooth to follow and 6x cheaper. This is the
 * knob to revisit if rooms get busy.
 */
const PLAYHEAD_THROTTLE_MS = 100;

const NAME_KEY = 'fabled.displayName';

/**
 * Collaborator colours (see --collab-* in styles/global.css). Deliberately
 * avoids the orange-red band: --accent means "you" everywhere else in the UI.
 */
export const COLLAB_COLORS = [
  '#4c9fe0',
  '#4fae64',
  '#a97bd0',
  '#d9a336',
  '#5ec8c0',
  '#d16fa8',
  '#7f8fd6',
  '#8fbf5a',
];

export interface PeerUser {
  name: string;
  color: string;
}

export interface Peer extends PeerUser {
  /** Awareness clientID — stable for the lifetime of a connection. */
  clientId: number;
  playhead: number | null;
}

/**
 * Colour from a hash of the name: stable across reloads, identical for every
 * viewer, and needs no storage. Distinct names can collide on a colour; the
 * name label alongside disambiguates.
 */
export function colorForName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return COLLAB_COLORS[Math.abs(hash) % COLLAB_COLORS.length];
}

/**
 * `null` = storage works and holds no name; `undefined` = storage is
 * unavailable. The distinction matters: an empty store means "generate a fresh
 * name", whereas an unusable one means "keep whatever we already generated",
 * and conflating them hands out a new name on every call.
 */
function readStoredName(): string | null | undefined {
  try {
    return localStorage.getItem(NAME_KEY);
  } catch {
    return undefined; // Safari private mode throws
  }
}

function writeStoredName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    /* not fatal — the name just won't survive a reload */
  }
}

/**
 * A display name for an anonymous visitor. Auto-assigned rather than prompted:
 * a modal in front of an editor someone just clicked into is friction, and the
 * avatar stack lets them rename in place.
 */
function generateGuestName(): string {
  return `Guest ${1000 + Math.floor(Math.random() * 9000)}`;
}

/**
 * The generated guest name for this session.
 *
 * localStorage writes can fail silently (Safari private mode), so relying on
 * storage alone would hand out a fresh `Guest NNNN` on every call — and
 * `useChat.send` calls this once per message, so each one would post under a
 * different name.
 */
let generatedName: string | null = null;

/** The local user's identity: their account name, or a stored/generated one. */
export function localIdentity(): PeerUser {
  const user = useSession.getState().user;
  if (user) return { name: user.username, color: colorForName(user.username) };

  const stored = readStoredName();
  if (typeof stored === 'string' && stored) return { name: stored, color: colorForName(stored) };

  // Storage is empty (null) or unusable (undefined). Only reuse the cached name
  // in the second case — an empty store is a genuinely new visitor.
  const name = (stored === undefined ? generatedName : null) ?? generateGuestName();
  generatedName = name;
  writeStoredName(name);
  return { name, color: colorForName(name) };
}

interface PresenceState {
  /** Everyone else in the room (never includes us). */
  peers: Peer[];
  /** Our own identity, so the UI can show and edit it. */
  self: PeerUser | null;
  setDisplayName(name: string): void;
}

export const usePresence = create<PresenceState>(() => ({
  peers: [],
  self: null,
  setDisplayName(name) {
    const trimmed = name.trim();
    if (!trimmed) return;
    // A signed-in user's name comes from their account; renaming is for guests.
    if (useSession.getState().user) return;
    generatedName = trimmed;
    writeStoredName(trimmed);
    const self = { name: trimmed, color: colorForName(trimmed) };
    usePresence.setState({ self });
    awareness?.setLocalStateField('user', self);
  },
}));

// ---------------------------------------------------------------------------
// Wiring — called by state/project.ts when the sync connection opens/closes.
// ---------------------------------------------------------------------------

let awareness: Awareness | null = null;
let cleanup: (() => void)[] = [];

/** Longest peer name we will render. Awareness state is peer-controlled. */
const MAX_NAME_LENGTH = 32;
/** Colours go straight into a style attribute, so constrain the shape. */
const COLOR_RE = /^#[0-9a-fA-F]{3,8}$/;

/**
 * Read the peer list out of awareness, defensively.
 *
 * Every field here is written by another client, and anyone holding the
 * collaboration link is another client. A non-string `name` reaches
 * `name.trim()` in the avatar stack; an arbitrary `color` reaches a style
 * attribute. Type-check rather than truthiness-check, and fall back rather than
 * propagate — a peer with a broken payload should be invisible or plain, never
 * a crash in someone else's topbar.
 */
function readPeers(aw: Awareness): Peer[] {
  const peers: Peer[] = [];
  for (const [clientId, state] of aw.getStates()) {
    if (clientId === aw.clientID) continue; // ourselves
    const user = (state as { user?: unknown } | undefined)?.user as
      | Record<string, unknown>
      | undefined;
    if (typeof user !== 'object' || user === null) continue;
    if (typeof user.name !== 'string' || !user.name) continue; // not announced yet
    const name = user.name.slice(0, MAX_NAME_LENGTH);
    const color =
      typeof user.color === 'string' && COLOR_RE.test(user.color)
        ? user.color
        : colorForName(name);
    const playhead = (state as { playhead?: unknown }).playhead;
    peers.push({
      clientId,
      name,
      color,
      playhead: typeof playhead === 'number' && Number.isFinite(playhead) ? playhead : null,
    });
  }
  peers.sort((a, b) => a.clientId - b.clientId); // stable render order
  return peers;
}

export function attachPresence(aw: Awareness): void {
  detachPresence();
  awareness = aw;

  const self = localIdentity();
  usePresence.setState({ self, peers: [] });
  aw.setLocalStateField('user', self);
  aw.setLocalStateField('playhead', usePlayback.getState().currentTime);

  // 'change', NOT 'update': update also fires on keepalive heartbeats, which
  // would re-render the peer list several times a second forever.
  const onChange = () => usePresence.setState({ peers: readPeers(aw) });
  aw.on('change', onChange);
  cleanup.push(() => aw.off('change', onChange));

  cleanup.push(watchPlayhead(aw));
}

export function detachPresence(): void {
  for (const fn of cleanup) fn();
  cleanup = [];
  // Clear our state so peers drop us immediately rather than after the 30s
  // awareness timeout.
  awareness?.setLocalState(null);
  awareness = null;
  usePresence.setState({ peers: [], self: null });
}

/**
 * Broadcast the local playhead, throttled. Follows the coalescing pattern used
 * for scrub seeks in components/timeline/Ruler.tsx: keep the newest value, emit
 * at most once per interval, and always flush the final position so a peer's
 * marker doesn't freeze mid-scrub.
 */
function watchPlayhead(aw: Awareness): () => void {
  let pending: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastSent: number | null = null;

  const flush = () => {
    // Clear as well as forget: flush is also called directly (on becoming
    // visible), and leaving the timeout queued would let schedule() start a
    // second one alongside it.
    if (timer !== null) clearTimeout(timer);
    timer = null;
    if (pending === null || pending === lastSent) return;
    lastSent = pending;
    aw.setLocalStateField('playhead', pending);
  };

  const schedule = (t: number) => {
    pending = t;
    // Nothing to say while paused at a position peers already have.
    if (t === lastSent) return;
    // A hidden tab may still be playing, but nobody is watching its marker.
    // Hold the value and send it when the tab comes back.
    if (document.visibilityState === 'hidden') return;
    if (timer === null) timer = setTimeout(flush, PLAYHEAD_THROTTLE_MS);
  };

  const unsubscribe = usePlayback.subscribe((s) => schedule(s.currentTime));

  // A hidden tab is still playing but nobody is watching its marker; stop the
  // traffic and resync on return.
  const onVisibility = () => {
    if (document.visibilityState === 'visible') flush();
  };
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    unsubscribe();
    document.removeEventListener('visibilitychange', onVisibility);
    if (timer !== null) clearTimeout(timer);
  };
}
